import { IServerSideGetRowsParams } from 'ag-grid-enterprise'
import { useAppDispatch, useAppSelector } from 'appState/store'
import { ObGrid } from '@outbound-ai/ursa'
import { LoadingBox } from '@outbound-ai/ursa'
import { IUser } from 'features/auth/types'
import { AutomationTypeEnum, CallTypeEnum, IBulkCalls, UseCaseEnumToUseCaseIdEnum } from 'features/calls/types'
import { archiveClaim, bulkOwner, startBulkCalls } from 'features/claims/claimsApi'
import { useAppealEnumDescriptions } from 'features/claims/hooks/useAppealEnumDescriptions'
import { refreshClaimCount, updateClaimsParams, updateCommonClaimsFilters } from 'features/claims/claimsSlice'
import { getLongAgentName } from 'features/claims/utils'
import { getClaimDetailPath } from 'features/claims/utils/claimsPathUtils'
import { useEntities } from 'features/entities/entityHooks'
import { EntityDictionary, WellKnownActorsEnum } from 'features/entities/types'
import { settingsServiceApiPaths, setUserPreference } from 'features/settings/settingsApi'
import { useTenantUserPreference } from 'features/settings/settingsHooks'
import { UserPreferencesKeysEnum } from 'features/settings/settingsTypes'
import { MouseEvent as ReactMouseEvent, useEffect, useMemo, useRef, useState } from 'react'
import { FaGripVertical, FaRegCheckSquare, FaRobot, FaTimes } from 'react-icons/fa'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button } from '@outbound-ai/ursa'
import styled, { css } from 'styled-components'
import { formatCallTypeLabel } from 'utils/cellFormat'
import { notificator } from 'utils/notificator/notificator'
import storage, { StorageEnum } from 'utils/storage'
import useWindowDimensions from 'utils/Utils'
import { getTntIdForNavigation } from 'utils/tenantUrlUtils'
import { BulkActionConfirmationModal } from './Internal/BulkActionConfirmationModal'
import { BulkAssignAndUnAssign, SelectedOaiUser } from './Internal/BulkAssignAndUnAssign'
import { CLAIMS_TABLE } from './Internal/ClaimsTableColumns'
import {
  DEFAULT_COLUMN_WITH_FILTER,
  ROW_MODEL_TYPE,
  SIDEBAR_COLUMN_FILTER,
  USER_COLUMN_STATE,
} from './Internal/ClaimsTableConstants'
import {
  getNoRowDataMessage,
  isSelectedClaimIsOwnedByCurrentUser,
  isSelectedClaimsHasOnlyNonOwners,
  storeColumPreferenceInLocalStorage,
} from './Internal/ClaimsTableHelper'
import { BulkType } from './Internal/ClaimsTableTypes'
import { createServerSideDataSource, onFilterOpened } from './Internal/ClaimsTableUtils'
import { ExternalCustomFilter } from './Internal/externalFilter/ExternalCustomFilter'
import { DEFAULT_FIELDS } from './Internal/externalFilter/filterTypes'
import { buildBatchContextFilterModel } from './Internal/externalFilter/filterUtils'

const CLAIMS_TABLE_ACTION_HOLDER = 264

const isGridDestroyed = (api: any): boolean => {
  if (!api) return true
  if (typeof api.isDestroyed === 'function') {
    return api.isDestroyed()
  }
  return false
}

export const ClaimsTable = () => {
  const location = useLocation()
  const gridRef = useRef<IServerSideGetRowsParams<any> | null>(null)
  const filterRef = useRef<HTMLDivElement | null>(null)
  const isMountedRef = useRef(true)
  const { windowWidth } = useWindowDimensions()

  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      gridRef.current = null
    }
  }, [])

  const { data: columnPreference, mutate: mutateColumnPreference } = useTenantUserPreference(
    UserPreferencesKeysEnum.claimsGridCommonColumns,
  )

  const [gridApi, setGridApi] = useState<any>(null)
  const [filterHeight, setFilterHeight] = useState<number>(120)

  const viewArchivedClaims = useAppSelector((state) => state.claims.viewArchivedClaims)
  const commonClaimsFilters = useAppSelector((state) => state.claims.commonClaimsFilters)
  const ascEnabled = useAppSelector((state) => state.auth.ascEnabled)

  const { batchCode, batchId } = useAppSelector((state) => state.claims.allClaimRouteUrl)
  const isBatchContext = Boolean(batchCode && batchId)

  const [gridColumnApi, setGridColumnApi] = useState<IServerSideGetRowsParams<any>>()
  const [isSelectedRows, setSelectedRows] = useState<any[]>([])
  const [isLoading, setLoading] = useState(false)
  const [isShowModal, setShowModal] = useState(false)
  const [bulkType, setBulkType] = useState<BulkType>()
  const [selectedRowCurrentPageNo, setSelectedRowCurrentPageNo] = useState<number | undefined>(undefined)
  const [lastPathSegments, setLastPathSegments] = useState({ section: '', status: '' })
  const [selectedUser, setSelectedUser] = useState<SelectedOaiUser>()

  const [isFetchStatusCalled, setFetchStatusCall] = useState(false)

  // Draggable action-holder state
  const [actionHolderPosition, setActionHolderPosition] = useState({ top: 150, left: 345 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })

  const currentUser: IUser = storage.get(StorageEnum.currentUser)
  const userId = currentUser.id

  const { data: entities } = useEntities()
  const appealEnumDescriptions = useAppealEnumDescriptions()

  const { data: filterPreference, mutate: mutateFiltersPreference } = useTenantUserPreference(
    UserPreferencesKeysEnum.claimsGridCommonFilters,
  )

  const filterString = filterPreference?.value
  const columPreferenceString = columnPreference?.value

  // Apply entity descriptions to column headers
  useEffect(() => {
    if (!entities) return
    const dictionary = new EntityDictionary(entities)
    CLAIMS_TABLE.forEach((column: any) => {
      if (column.entityId) {
        const entity = dictionary.GetEntity(column.entityId)
        if (entity?.description) {
          column.headerName = entity.description
        }
      }
    })
  }, [entities])

  // Sync column preferences from localStorage -> user preferences
  const columPreferenceLoaded = useRef(true)
  useEffect(() => {
    if (columPreferenceLoaded.current || isFetchStatusCalled) {
      const columnLocalState = localStorage.getItem(USER_COLUMN_STATE)
      if (
        columnLocalState &&
        columnPreference &&
        (!columPreferenceString || columnLocalState !== columPreferenceString)
      ) {
        try {
          const userColumnState = {
            key: UserPreferencesKeysEnum.claimsGridCommonColumns,
            value: columnLocalState,
          }
          setUserPreference(settingsServiceApiPaths.tenantUserPreferencesPut(), userColumnState)
          mutateColumnPreference(userColumnState, false)
          columPreferenceLoaded.current = false
        } catch (error: any) {
          console.log('Column preference sync error:', error)
        }
        setFetchStatusCall(false)
      }
    }
  }, [columnPreference, isFetchStatusCalled, columPreferenceString, mutateColumnPreference])

  // Apply column state when gridApi becomes available
  useEffect(() => {
    if (!gridApi || !gridApi.columnApi) return

    const columnLocalState = localStorage.getItem(USER_COLUMN_STATE)
    if (columnLocalState) {
      const columnPreferenceObj = JSON.parse(columnLocalState)
      gridApi.api.applyColumnState({ state: columnPreferenceObj, applyOrder: true })
      gridApi.api.moveColumns(['serialNumber', 'visitId'], 1)
    }
  }, [gridApi])

  // Measure filter height dynamically for layout
  useEffect(() => {
    const measureFilterHeight = () => {
      if (filterRef.current) {
        const height = filterRef.current.offsetHeight
        setFilterHeight(height + 16)
      }
    }

    measureFilterHeight()

    const resizeObserver = new ResizeObserver(measureFilterHeight)
    if (filterRef.current) {
      resizeObserver.observe(filterRef.current)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  // Set / refresh datasource when gridApi, batchCode, or viewArchivedClaims change
  useEffect(() => {
    if (!gridApi || !gridApi.api || isGridDestroyed(gridApi.api)) return

    const dataSource = createServerSideDataSource(dispatch, batchCode, viewArchivedClaims)
    gridApi.api.setGridOption('serverSideDatasource', dataSource)
    gridApi.api.refreshServerSide({ purge: true })
  }, [gridApi, batchCode, viewArchivedClaims, dispatch])

  /**
   * Initial Load OB Grid
   */
  const onGridReady = async (params: IServerSideGetRowsParams) => {
    setGridApi(params)
    setGridColumnApi(params)
    gridRef.current = params
    dispatch(updateClaimsParams({ params }))
    const columnLocalState = JSON.parse(localStorage.getItem(USER_COLUMN_STATE) || 'null')

    if (columPreferenceString) {
      const columnPreferenceObj = JSON.parse(columPreferenceString)
      params.api.applyColumnState({ state: columnPreferenceObj, applyOrder: true })
    } else if (columnLocalState && columnLocalState.length > 0) {
      params.api.applyColumnState({ state: columnLocalState, applyOrder: true })
    }

    if (isBatchContext && batchCode) {
      const batchFilters = buildBatchContextFilterModel(batchCode, batchId, DEFAULT_FIELDS)
      params.api.setFilterModel(batchFilters)
      dispatch(updateCommonClaimsFilters({ filters: batchFilters }))
    } else if (filterString) {
      const parsedFilters = JSON.parse(filterString)
      params.api.setFilterModel(parsedFilters)
      dispatch(updateCommonClaimsFilters({ filters: parsedFilters }))
    }
    params.api.moveColumns(['serialNumber', 'visitId'], 1)

    const dataSource = createServerSideDataSource(dispatch, batchCode, viewArchivedClaims)
    params.api.setGridOption('serverSideDatasource', dataSource)

    if (!columnLocalState) {
      storeColumPreferenceInLocalStorage(params)
    }
  }

  const onFilterChanged = async () => {
    if (!gridApi?.api || isGridDestroyed(gridApi.api)) return

    const selectHeader = document.querySelector('#selectGridHeader') as HTMLInputElement | null

    if (isSelectedRows?.length > 0 || selectHeader) {
      deselectClaims()
    }

    gridApi.api.paginationGoToPage(0)

    const agGridFilters = gridApi.api.getFilterModel() || {}

    // Get Redux filters (contains externalized filters with metadata like preset selections)
    const reduxFilters = commonClaimsFilters || {}

    // List of externalized fields that have metadata we need to preserve
    const externalizedFields = [
      'visitId',
      'patientLastName',
      'memberId',
      'patientId',
      'uniqueIdentifier',
      'admitDate',
      'oaiWorked',
      'batchCode',
      'claimStatusPayer',
      'appealStatus',
      'owner',
    ]

    // Merge: Start with AG Grid filters (includes column filters)
    // Then override with Redux filters for externalized fields (preserves metadata)
    const mergedFilters: any = { ...agGridFilters }

    externalizedFields.forEach((field) => {
      if (reduxFilters[field] && (!isBatchContext || agGridFilters[field])) {
        mergedFilters[field] = reduxFilters[field]
      }
    })

    if (JSON.stringify(mergedFilters) !== filterString) {
      const newFilters = {
        key: UserPreferencesKeysEnum.claimsGridCommonFilters,
        value: JSON.stringify(mergedFilters),
      }
      await setUserPreference(settingsServiceApiPaths.tenantUserPreferencesPut(), newFilters)
      await mutateFiltersPreference(newFilters, false)
    }

    // Update Redux to stay in sync with all filters (both externalized and column filters)
    dispatch(updateCommonClaimsFilters({ filters: mergedFilters }))

    // refresh grid based on new filters
    gridApi.api.refreshServerSide({ purge: true })
  }

  const onSelectionChanged = (api: any) => {
    const pageNo = api.paginationGetCurrentPage()
    setSelectedRowCurrentPageNo(pageNo)
    const selectedRows = api.getSelectedRows()
    setSelectedRows(selectedRows)
  }

  const deselectClaims = () => {
    if (gridApi?.api && !isGridDestroyed(gridApi.api)) {
      gridApi.api.deselectAll()
      const x = document.querySelector('#selectGridHeader') as HTMLInputElement | null
      if (x) {
        x.checked = false
      }
    }
    setSelectedRows([])
  }

  useEffect(() => {
    const pathSegments = location.pathname.split('/')
    const section = pathSegments[2] || ''
    const status = pathSegments[3] || ''
    if (
      (lastPathSegments.section && lastPathSegments.section !== section) ||
      (lastPathSegments.status && lastPathSegments.status !== status)
    ) {
      if (section !== lastPathSegments.section || status !== lastPathSegments.status) {
        deselectClaims()
      }
    }
    setLastPathSegments({ section, status })
  }, [location.pathname])

  const handleSuccess = (successMessage: string) => {
    setLoading(false)
    dispatch(refreshClaimCount({ refreshCount: true }))
    deselectClaims()
    notificator.notify({
      type: 'success',
      heading: successMessage,
    })
  }

  const handleError = (err: any) => {
    setLoading(false)
    notificator.notify({
      type: 'error',
      heading: err.response?.data?.detail || err.message,
    })
    deselectClaims()
  }

  const onArchive = async () => {
    try {
      setLoading(true)
      const claimArchive = {
        claims: isSelectedRows.map((claim: any) => ({
          oaiClaimId: claim.oaiClaimId,
          archived: claim.archived ? false : true,
        })),
      }
      const res = await archiveClaim(claimArchive)
      if (gridApi?.api && !isGridDestroyed(gridApi.api)) {
        gridApi.api.refreshServerSide({ purge: true })
      }
      handleSuccess(`Successfully ${!res[0].archived ? 'unarchived' : 'archived'}  ${res.length} claim(s)`)
    } catch (err) {
      handleError(err)
    }
  }

  const fetchStatus = async () => {
    const isSelectedRowsWithECS = isSelectedRows.filter((claim: any) => {
      return [
        AutomationTypeEnum.fullCSI,
        AutomationTypeEnum.los1,
        AutomationTypeEnum.los4,
        AutomationTypeEnum.los5,
        AutomationTypeEnum.los7,
      ].some((type: any) => claim?.levelOfSupport?.includes(type))
    })
    try {
      setLoading(true)
      const fetchStatusReq: IBulkCalls = {
        forceOwnership: false,
        data: isSelectedRowsWithECS.map((claim: any) => {
          return {
            claimId: claim.oaiClaimId,
            useCase: 1,
          }
        }),
      }
      const res = await startBulkCalls(fetchStatusReq)
      handleSuccess(`Successfully queued ${res.data.length} claim(s) to Fetch ECS `)
    } catch (err) {
      handleError(err)
    }
  }

  const fetchEBV = async () => {
    const isSelectedRowsWithEBV = isSelectedRows.filter((claim: any) => {
      return [AutomationTypeEnum.los1, AutomationTypeEnum.los2, AutomationTypeEnum.los3, AutomationTypeEnum.los4].some(
        (type: any) => claim?.levelOfSupport?.includes(type),
      )
    })
    try {
      setLoading(true)
      const fetchStatusReq: IBulkCalls = {
        forceOwnership: false,
        data: isSelectedRowsWithEBV.map((claim: any) => {
          return {
            claimId: claim.oaiClaimId,
            useCase: 2,
          }
        }),
      }
      const res = await startBulkCalls(fetchStatusReq)
      handleSuccess(`Successfully queued ${res.data.length} claim(s) to Fetch EBV `)
    } catch (err) {
      handleError(err)
    }
  }

  const fetchEOB = async () => {
    const isSelectedRowsWithEOB = isSelectedRows.filter((claim: any) => {
      return [
        AutomationTypeEnum.los4,
        AutomationTypeEnum.los5,
        AutomationTypeEnum.los6,
        AutomationTypeEnum.fullCSI,
        AutomationTypeEnum.los1,
        AutomationTypeEnum.los8,
      ].some((type: any) => claim?.levelOfSupport?.includes(type))
    })
    try {
      setLoading(true)
      const fetchStatusReq: IBulkCalls = {
        forceOwnership: false,
        data: isSelectedRowsWithEOB.map((claim: any) => {
          return {
            claimId: claim.oaiClaimId,
            useCase: 3,
          }
        }),
      }
      const res = await startBulkCalls(fetchStatusReq)
      handleSuccess(`Successfully queued ${res.data.length} claim(s) for Fetch ECS and/or EOB `)
    } catch (err) {
      handleError(err)
    }
  }

  const fetchASC = async () => {
    const isSelectedRowsWithASC = isSelectedRows.filter((claim: any) => {
      return [AutomationTypeEnum.los9, AutomationTypeEnum.los10, AutomationTypeEnum.los11, AutomationTypeEnum.ASC].some(
        (type: any) => claim?.levelOfSupport?.includes(type),
      )
    })
    try {
      setLoading(true)
      const fetchStatusReq: IBulkCalls = {
        forceOwnership: false,
        data: isSelectedRowsWithASC.map((claim: any) => {
          return {
            claimId: claim.oaiClaimId,
            useCase: 7,
          }
        }),
      }
      const res = await startBulkCalls(fetchStatusReq)
      handleSuccess(`Successfully queued ${res.data.length} claim(s) for Fetch ASC `)
    } catch (err) {
      handleError(err)
    }
  }

  const getRowId = useMemo(() => {
    return (params: any) => {
      const data = params.data
      return data.oaiClaimId
    }
  }, [])

  useEffect(() => {
    deselectClaims()
  }, [viewArchivedClaims])

  const handleBulkAction = (type: BulkType) => {
    const currentUserClaims = getClaimsOwnedByCurrentUser(userId!)?.length
    if (currentUserClaims === isSelectedRows.length) {
      setFetchStatusCall(true)
      if (type === 'fetchEbv') {
        fetchEBV()
      } else if (type === 'fetchECS') {
        fetchStatus()
      } else if (type === 'fetchEOB') {
        fetchEOB()
      } else if (type === 'fetchASC') {
        fetchASC()
      }
    } else {
      setBulkType(type)
      const selectedClaimHasOnlyNonOwners = isSelectedClaimsHasOnlyNonOwners(isSelectedRows, userId!)

      if (selectedClaimHasOnlyNonOwners && type === 'takeOwnership') {
        const actorName = getLongAgentName(currentUser)

        const bulkOwnership = {
          claims: isSelectedRows.map((claim: any) => {
            return {
              oaiClaimId: claim.oaiClaimId,
              activityId: '3dca2d43-aaf1-4e36-8636-22c706d66146',
              actorName: actorName,
              actorId: WellKnownActorsEnum.rcmAgent,
              remarks: 'Claims Ownership',
              entityVersions: [
                {
                  entity: {
                    entityId: 'a59bc4cc-7e76-40ed-804c-fc7bfdf5655e',
                  },
                  entityValue: actorName,
                },
                {
                  entity: {
                    entityId: 'ec6795a7-e04b-4f8f-8753-cb013936f4f2',
                  },
                  entityValue: currentUser.id,
                },
              ],
            }
          }),
        }
        bulkOwner(bulkOwnership).then((res: any) => {
          if (res && gridApi?.api && !isGridDestroyed(gridApi.api)) {
            gridApi.api.refreshServerSide({ purge: true })
            notificator.notify({
              type: 'success',
              heading: `Successfully assigned  ${res.length} claim(s)`,
            })
            setLoading(false)
          }
        })
        return
      } else {
        setFetchStatusCall(true)
        setShowModal(true)
      }
    }
  }

  const cancelBulkModal = (status: string | undefined) => {
    if (status === 'CLEAR') {
      deselectClaims()
    }

    setShowModal(false)
    setLoading(false)
  }

  const noRowsOverlayComponentParams = useMemo(() => {
    if (gridColumnApi) {
      return {
        noRowsMessageFunc: () => getNoRowDataMessage(),
      }
    }
    return undefined
  }, [gridColumnApi, dispatch])

  const redirectToClaimDetail = (e: any) => {
    const tenantId = getTntIdForNavigation()
    navigate(getClaimDetailPath(tenantId, e.data.claimId))
  }

  const updateState = () => {
    if (!gridApi?.api || isGridDestroyed(gridApi.api)) return

    const ss = gridApi.api.getSelectedRows()
    const hasOwners = isSelectedClaimIsOwnedByCurrentUser(ss, userId!)
    if (hasOwners && hasOwners.length <= 0) {
      setSelectedRows(ss)
    }
  }

  const getClaimsOwnedByCurrentUser = (ownerId: string, type?: BulkType) => {
    const selectedRows = gridApi && gridApi.api && !isGridDestroyed(gridApi.api) ? gridApi.api.getSelectedRows() : []
    const claimsOwnedByCurrentUser = selectedRows.filter((claim: any) => claim?.ownerId === ownerId)
    let AutomationType: AutomationTypeEnum[]

    if (type === 'fetchEbv') {
      AutomationType = [
        AutomationTypeEnum.los1,
        AutomationTypeEnum.los2,
        AutomationTypeEnum.los3,
        AutomationTypeEnum.los4,
      ]
    } else if (type === 'fetchECS') {
      AutomationType = [
        AutomationTypeEnum.fullCSI,
        AutomationTypeEnum.los1,
        AutomationTypeEnum.los4,
        AutomationTypeEnum.los5,
        AutomationTypeEnum.los7,
      ]
    } else if (type === 'fetchEOB') {
      AutomationType = [
        AutomationTypeEnum.los4,
        AutomationTypeEnum.los5,
        AutomationTypeEnum.los6,
        AutomationTypeEnum.los8,
      ]
    } else if (type === 'fetchASC') {
      AutomationType = [
        AutomationTypeEnum.los9,
        AutomationTypeEnum.los10,
        AutomationTypeEnum.los11,
        AutomationTypeEnum.ASC,
      ]
    } else {
      AutomationType = []
    }

    if (!AutomationType || AutomationType.length === 0) {
      return claimsOwnedByCurrentUser
    }

    return claimsOwnedByCurrentUser.filter((claim: any) => {
      return AutomationType.some((t: any) => claim?.levelOfSupport?.includes(t))
    })
  }

  // Drag handlers for action-holder
  const handleMouseDown = (e: ReactMouseEvent) => {
    setIsDragging(true)
    setDragStart({
      x: e.clientX - actionHolderPosition.left,
      y: e.clientY - actionHolderPosition.top,
    })
    e.preventDefault()
  }

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return

    const newPosition = {
      left: Math.max(0, e.clientX - dragStart.x),
      top: Math.max(0, e.clientY - dragStart.y),
    }

    const actionHolderWidth = Math.min(800, windowWidth - CLAIMS_TABLE_ACTION_HOLDER)
    const actionHolderHeight = 80
    const maxLeft = Math.max(0, window.innerWidth - actionHolderWidth - 20)
    const maxTop = Math.max(0, window.innerHeight - actionHolderHeight - 20)

    setActionHolderPosition({
      left: Math.min(newPosition.left, maxLeft),
      top: Math.min(newPosition.top, maxTop),
    })
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = 'grabbing'

      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        document.body.style.cursor = 'default'
      }
    }
  }, [isDragging, dragStart.x, dragStart.y])

  const LoadingBar = !filterPreference || !columnPreference || isLoading

  return (
    <>
      {isShowModal && (
        <BulkActionConfirmationModal
          selectedRow={isSelectedRows}
          type={bulkType!}
          setShow={(status: string | undefined) => cancelBulkModal(status)}
          GridApi={gridApi}
          oaiUserName={selectedUser?.oaiUserName}
          oaiUserId={selectedUser?.oaiUserId}
        />
      )}

      <Div filterHeight={filterHeight}>
        {LoadingBar && <LoadingBox message="Loading preferences..." />}
        {isSelectedRows && isSelectedRows.length > 0 && (
          <div
            className={`action-holder ${isDragging ? 'dragging' : ''}`}
            style={{
              minWidth: `${windowWidth - CLAIMS_TABLE_ACTION_HOLDER}px`,
              top: `${actionHolderPosition.top}px`,
              left: `${actionHolderPosition.left}px`,
            }}
          >
            <div className="drag-handle" onMouseDown={handleMouseDown} title="Drag to move">
              <FaGripVertical />
            </div>
            <div className="actions">
              <span>
                {isSelectedRows.length} Claim{isSelectedRows.length > 1 ? 's' : ''} selected{' '}
              </span>{' '}
              <>
                <BulkAssignAndUnAssign
                  oaiUser={(oaiUser) => {
                    setSelectedUser(oaiUser)
                    const type: BulkType = oaiUser?.oaiUserName ? 'takeOwnership' : 'unAssign'
                    setBulkType(type)
                    setShowModal(true)
                  }}
                />
                <Button
                  width="7.125rem"
                  variant="info"
                  radius="rounded"
                  size="small"
                  onClick={() => handleBulkAction('fetchECS')}
                  disabled={
                    getClaimsOwnedByCurrentUser(userId!, 'fetchECS').length <= 0 ? true : false || viewArchivedClaims
                  }
                >
                  <FaRobot /> {formatCallTypeLabel(CallTypeEnum.unattended, UseCaseEnumToUseCaseIdEnum.CSI)}
                </Button>
                <Button
                  width="9.875rem"
                  variant="info"
                  radius="rounded"
                  size="small"
                  onClick={() => handleBulkAction('fetchEOB')}
                  disabled={
                    getClaimsOwnedByCurrentUser(userId!, 'fetchEOB').length <= 0 ? true : false || viewArchivedClaims
                  }
                >
                  <FaRobot /> {formatCallTypeLabel(CallTypeEnum.unattended, UseCaseEnumToUseCaseIdEnum.EOB)}
                </Button>
                {ascEnabled && (
                  <Button
                    width="9.875rem"
                    variant="info"
                    radius="rounded"
                    size="small"
                    onClick={() => handleBulkAction('fetchASC')}
                    disabled={
                      getClaimsOwnedByCurrentUser(userId!, 'fetchASC').length <= 0 ? true : false || viewArchivedClaims
                    }
                  >
                    <FaRobot /> {formatCallTypeLabel(CallTypeEnum.unattended, UseCaseEnumToUseCaseIdEnum.ASC)}
                  </Button>
                )}
              </>
              <Button width="6.125rem" variant="primary-outline" radius="rounded" size="small" onClick={onArchive}>
                <FaRegCheckSquare />
                {viewArchivedClaims ? 'Unarchive' : 'Archive'}
              </Button>
            </div>
            <div className="overflow-hidden">
              <Button variant="tertiary" onClick={deselectClaims} width="11.5rem">
                <FaTimes /> Deselect & Cancel
              </Button>
            </div>
          </div>
        )}
        <div ref={filterRef} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {gridApi?.api && gridApi?.columnApi && !isGridDestroyed(gridApi.api) && (
            <ExternalCustomFilter
              api={gridApi.api}
              fields={
                {
                  // map to real column fields here if you need overrides
                } as any
              }
            />
          )}
        </div>
        {!LoadingBar && (
          <ObGrid
            reactiveCustomComponents={true}
            context={{
              appealSubmissionMethodDescriptions: appealEnumDescriptions.appeal_submission_method,
              appealStatusDescriptions: appealEnumDescriptions.appeal_status,
              levelOfAppealDescriptions: appealEnumDescriptions.level_of_appeal,
            }}
            columnDefs={CLAIMS_TABLE as any}
            rowModelType={ROW_MODEL_TYPE}
            onGridReady={onGridReady as any}
            defaultColDef={DEFAULT_COLUMN_WITH_FILTER as any}
            rowSelection="multiple"
            rowHeight={50}
            getRowId={getRowId}
            onCellClicked={(e) => {
              if (!gridApi?.api || isGridDestroyed(gridApi.api)) return

              const selected = gridApi.api.getSelectedRows()
              if (selected.length === 1 && selected[0].claimId === e.data.claimId) {
                localStorage.setItem('isRowSelected', JSON.stringify(selected[0].claimId))
              } else {
                localStorage.setItem('isRowSelected', JSON.stringify(null))
              }
              localStorage.setItem('oaiClaimId', JSON.stringify(e.data.claimId))
              if (e.colDef.field !== 'serialNumber' && selected.length <= 1) {
                redirectToClaimDetail(e)
              }
            }}
            onColumnMoved={() => {
              storeColumPreferenceInLocalStorage(gridApi)
            }}
            onColumnVisible={() => {
              storeColumPreferenceInLocalStorage(gridApi)
            }}
            onPaginationChanged={(params) => {
              const currentPage = params.api.paginationGetCurrentPage()
              const selectedRows = params.api.getSelectedRows()
              const selectedClaimIds = selectedRows && selectedRows.map((row: any) => row.claimId)

              if (selectedClaimIds.length > 0) {
                const paramsClaimIds: string[] = []
                params.api.forEachNode((node: any) => paramsClaimIds.push(node?.data?.claimId))
                const currentPageClaimIdsSet = new Set(paramsClaimIds)
                const filteredSelectedRowIds = selectedClaimIds.filter((id: any) => currentPageClaimIdsSet.has(id))

                gridApi?.api?.forEachNode((node: any) => {
                  if (node.data && node.id) {
                    const isSelected = filteredSelectedRowIds.includes(node.data.claimId)
                    node.setSelected(isSelected)
                  }
                })
                setSelectedRows(params.api.getSelectedRows())
              }
              if (currentPage !== 0 && currentPage !== selectedRowCurrentPageNo) {
                deselectClaims()
              }

              const filterModel = params.api.getFilterModel()
              if (filterModel?.owner) {
                const currentUsername = `${currentUser.given_name} ${currentUser.family_name}`
                let currentUserOwnerSelected = false

                if (filterModel.owner.values && Array.isArray(filterModel.owner.values)) {
                  currentUserOwnerSelected = filterModel.owner.values.includes(currentUsername)
                } else if (filterModel.owner.filter && typeof filterModel.owner.filter === 'string') {
                  currentUserOwnerSelected = currentUsername
                    .toLowerCase()
                    .includes(filterModel.owner.filter.toLowerCase())
                }

                if (!currentUserOwnerSelected) {
                  deselectClaims()
                }
              }
              if (params.api.getSelectedRows()?.length > 0) {
                updateState()
              }
              if (params.newPage) {
                const newPage = params.api.paginationGetCurrentPage()
                localStorage.setItem('currentPage', JSON.stringify(newPage))
              }
            }}
            onFirstDataRendered={(params) => {
              const currentPage = JSON.parse(localStorage.getItem('currentPage') || '0')
              params.api.paginationGoToPage(currentPage)
              const selectedRowId = localStorage.getItem('isRowSelected')
              if (currentPage === 0) {
                params.api.forEachNode((node: any) => {
                  if (selectedRowId && node.data?.claimId === JSON.parse(selectedRowId)) {
                    node.setSelected(true)
                  }
                })
              } else {
                params.api.addEventListener('paginationChanged', () => {
                  if (currentPage === params.api.paginationGetCurrentPage()) {
                    params.api.forEachNode((node: any) => {
                      if (selectedRowId && node.data?.claimId === JSON.parse(selectedRowId)) {
                        node.setSelected(true)
                      }
                    })
                  }
                })
              }
            }}
            getRowStyle={(params) => {
              if (params.data != undefined) {
                if (params.data.claimId === JSON.parse(localStorage.getItem('oaiClaimId') || 'null')) {
                  return {
                    border: '1px solid #227bbb',
                  }
                }
              }
              return undefined
            }}
            onFilterOpened={(e) => {
              onFilterOpened(gridApi, e)
            }}
            suppressRowClickSelection={true}
            onFilterChanged={onFilterChanged}
            noRowsOverlayComponent={getNoRowDataMessage}
            onSortChanged={(e) => {
              storeColumPreferenceInLocalStorage(gridApi)
              if (isSelectedRows?.length > 0) {
                e.api.deselectAll()
              }
              e.api.paginationGoToPage(0)
            }}
            onSelectionChanged={() => {
              if (gridApi?.api && !isGridDestroyed(gridApi.api)) {
                onSelectionChanged(gridApi.api)
              }
            }}
            cacheBlockSize={100}
            noRowsOverlayComponentParams={noRowsOverlayComponentParams}
            sideBar={SIDEBAR_COLUMN_FILTER}
          />
        )}
      </Div>
    </>
  )
}

const Div = styled.div<{ filterHeight: number }>`
  ${({ theme, filterHeight }) => css`
    height: calc(
      100vh - ${theme.constants.navbarHeight} - ${theme.constants.pageTitleHeight} - ${theme.constants.tableHeadHeight} -
        ${filterHeight}px + 40px
    );
  `}
  padding: 0 1rem 1.2rem 0;
  .ag-side-buttons {
    width: 48px;
    color: #a9abae;
    font-size: 14px;
    font-weight: 600;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.14;
    letter-spacing: normal;
    text-align: center;
  }
  .action-holder {
    display: flex;
    min-width: fit-content !important;
    gap: 0.5rem;
    align-items: center;
    justify-content: space-between;
    position: fixed;
    z-index: 101;
    margin-right: 1rem;
    padding: ${({ theme }) => theme.spacing.xs};
    border-radius: 8px;
    box-shadow: 0 8px 14px 0 rgba(0, 0, 0, 0.06);
    border: solid 1px ${({ theme }) => theme.colors.grey.lighter};
    background-color: ${({ theme }) => theme.colors.neutral.lightest};
    user-select: none;
    transition: box-shadow 0.2s;

    &.dragging {
      box-shadow: 0 12px 20px 0 rgba(0, 0, 0, 0.12);
      z-index: 102;
    }

    .drag-handle {
      cursor: grab;
      color: ${({ theme }) => theme.colors.grey.dark};
      padding: 0.25rem;
      display: flex;
      align-items: center;
      border-radius: 4px;
      transition: background-color 0.2s;

      &:hover {
        background-color: ${({ theme }) => theme.colors.grey.lighter};
      }

      &:active {
        cursor: grabbing;
        background-color: ${({ theme }) => theme.colors.grey.light};
      }
    }

    .actions {
      display: flex;
      gap: 1rem;
      align-items: center;
      button {
        white-space: nowrap;
      }
      span {
        ${({ theme }) => theme.typography.m.medium};
        color: ${({ theme }) => theme.colors.neutral.neutral};
        white-space: nowrap;
      }
    }
  }

  .ag-header-cell.externalized-filter .ag-header-icon {
    display: none !important;
  }

  // ObGrid sets .ag-overlay to pointer-events: all, and in AG Grid v31 the overlay also covers the header.
  // Let clicks pass through the no-rows overlay so column filters stay usable when a filter returns no data.
  .ag-overlay:has(.ag-overlay-no-rows-wrapper) {
    pointer-events: none;
  }
`
